/** Reporting engine — Focus Explorer.
 *
 * Click a widget to lock onto it and read the tickets behind the number. It is a
 * READ view — the widget's own saved filters are what it shows, and there is no
 * live slicing here. Filters are configured where a widget is built or edited
 * (WidgetBuilder), which is the only place they can be pinned onto the shared
 * global template.
 *
 * The drill-down is the ticket table and nothing else. It used to re-draw the
 * clicked widget at size above a summary strip, which read as an awkward copy of
 * the tile the customer had just clicked (issue #91).
 *
 * The listed tickets are the clicked widget's own cohort (cohortForWidget): the
 * month's OPENED tickets for everything bar "Tickets completed", which lists the
 * work finished this month. The widget's filters apply inside that cohort, so the
 * popout can never show a ticket the tile did not count.
 */

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";

import { TicketDetail } from "../pages/Tickets";
import type { ReportingTicketRow } from "../services/api";

import {
  C,
  CSAT_TIER_COLOR,
  CSAT_TIER_LABELS,
  CSAT_TIER_ORDER,
  MONO,
  SANS,
  applyFilters,
  cohortDay,
  cohortForWidget,
  cohortRows,
  seedFiltersForWidget,
  tierOf,
} from "./data";
import { SortableTable } from "./tableSort";
import type { Column } from "./tableSort";
import type { Drill, Filters, Widget } from "./types";
import { PriorityPill, priorityRank } from "./widgets";

type Row = ReportingTicketRow;

function widgetTitle(w: Widget): string {
  return w.title || "Widget";
}

const td: CSSProperties = { padding: "8px 8px", borderBottom: `1px dotted ${C.rule}`, color: C.ink };
const th: CSSProperties = {
  textAlign: "left",
  padding: "8px 8px",
  borderBottom: `2px solid ${C.ink}`,
  fontWeight: 500,
  fontSize: 9.5,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: C.stone,
  fontFamily: MONO,
  background: "#fff",
};

/** A ticket reference, linked only when the ticket detail page can actually
 * serve it.
 *
 * Reporting counts tickets from the alert queues that the ticket list hides, and
 * the detail endpoint 404s those for staff and clients alike. Linking them would
 * send a customer from a figure that says they own the ticket straight to a
 * not-found page, so they render as plain text with the reason on hover.
 *
 * A plain click opens the ticket over this drill-down, so reading a ticket costs
 * nothing to undo — closing it puts the customer back on the same list, still on
 * the reports page (issue #91). It stays a real href, so ⌘/ctrl/middle-click
 * still opens the Tickets page in a new tab for anyone who wants one. */
function TicketRef({ row, onOpen }: { row: Row; onOpen: (ticketNumber: string) => void }) {
  const ref = row.ticketNumber ?? row.id;
  if (ref == null) return null;
  if (row.detailAvailable === false) {
    return (
      <span
        style={{ color: C.stone }}
        title="Automated alert ticket — counted in your volume, but not shown in the ticket list"
      >
        {ref}
      </span>
    );
  }
  const openInPlace = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    // Leave the browser's own "open elsewhere" gestures alone.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onOpen(String(ref));
  };
  return (
    <a
      href={`/tickets/${ref}`}
      onClick={openInPlace}
      style={{ color: C.purple, textDecoration: "none" }}
      title="Open this ticket here"
    >
      {ref}
    </a>
  );
}

/** The Customer Thermometer rating on a ticket, in the survey's own colour.
 *
 * Falls back to the raw temperature when the tier cannot be mapped, so a value we
 * have not seen before is still shown rather than silently blanked. */
function CsatCell({ row }: { row: Row }) {
  if (!row.csat) return <span style={{ color: C.stone }}>&nbsp;</span>;
  const tier = tierOf(row);
  const colour = tier ? CSAT_TIER_COLOR[tier] : C.stone;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11 }}>
      <i style={{ width: 8, height: 8, borderRadius: 2, background: colour, display: "inline-block" }} />
      <span style={{ color: C.ink }}>{tier ? CSAT_TIER_LABELS[tier] : row.csat}</span>
    </span>
  );
}

/** Every column is click-to-sort (issue #121); the incoming order — the cohort's
 *  own day, newest first — is what the table shows until a header is clicked, and
 *  a third click returns to it. */
function useExplorerColumns(onOpenTicket: (ticketNumber: string) => void): Column<Row>[] {
  return useMemo(
    () => [
      {
        key: "id",
        label: "ID",
        value: (t) => t.ticketNumber ?? (t.id != null ? String(t.id) : null),
        cell: (t) => <TicketRef row={t} onOpen={onOpenTicket} />,
        cellStyle: { fontFamily: MONO, whiteSpace: "nowrap" },
      },
      { key: "subject", label: "Subject", value: (t) => t.subject, cell: (t) => t.subject },
      {
        key: "contact",
        label: "Contact",
        value: (t) => t.contact,
        cell: (t) => t.contact,
        cellStyle: { color: C.mud, whiteSpace: "nowrap" },
      },
      {
        key: "priority",
        label: "Priority",
        value: priorityRank,
        cell: (t) => <PriorityPill priority={t.priority} small />,
      },
      {
        key: "subType",
        label: "Sub-category",
        value: (t) => t.subType,
        cell: (t) => t.subType,
        cellStyle: { color: C.mud, whiteSpace: "nowrap" },
      },
      {
        key: "source",
        label: "Source",
        value: (t) => t.source,
        cell: (t) => t.source,
        cellStyle: { color: C.mud },
      },
      {
        key: "status",
        label: "Status",
        value: (t) => t.status,
        cell: (t) => t.status,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        key: "response",
        label: "Response",
        right: true,
        value: (t) => t.firstResponseMins,
        cell: (t) => (t.firstResponseMins != null ? `${t.firstResponseMins}m` : "—"),
        cellStyle: { fontFamily: MONO, whiteSpace: "nowrap" },
      },
      {
        key: "resolve",
        label: "Resolve",
        right: true,
        value: (t) => t.resolutionHours,
        cell: (t) => (t.resolutionHours != null ? `${t.resolutionHours}h` : "—"),
        cellStyle: { fontFamily: MONO, whiteSpace: "nowrap", color: C.stone },
      },
      {
        key: "engineer",
        label: "Staff member",
        value: (t) => t.engineer,
        cell: (t) => t.engineer,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        key: "sla",
        label: "SLA",
        right: true,
        // Misses first on the first click: "which ones did we miss?" is the
        // question this column exists to answer. Unjudged tickets sort last.
        value: (t) => (t.slaResponseMet === false ? 0 : t.slaResponseMet === true ? 1 : null),
        cell: (t) => (
          <span style={{ fontFamily: MONO, fontSize: 10, color: t.slaResponseMet === false ? C.orange : C.forest }}>
            {t.slaResponseMet == null ? "—" : t.slaResponseMet ? "✓ met" : "✕ miss"}
          </span>
        ),
      },
      {
        // The Customer Thermometer rating itself, so a satisfaction drill-down
        // shows WHAT each customer said, not just that they answered. Blank when
        // the survey went unanswered, and those rows sort last. Sorted by the
        // survey's own scale (best first), not by the label alphabetically.
        key: "rating",
        label: "Rating",
        value: (t) => {
          const tier = tierOf(t);
          return tier ? CSAT_TIER_ORDER.indexOf(tier) : null;
        },
        cell: (t) => <CsatCell row={t} />,
        cellStyle: { whiteSpace: "nowrap" },
      },
    ],
    [onOpenTicket],
  );
}

function ExplorerTable({
  tickets,
  onOpenTicket,
}: {
  tickets: Row[];
  onOpenTicket: (ticketNumber: string) => void;
}) {
  const columns = useExplorerColumns(onOpenTicket);
  return (
    <SortableTable
      rows={tickets}
      columns={columns}
      rowKey={(t, i) => t.ticketNumber ?? String(t.id ?? `row-${i}`)}
      tableStyle={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}
      thStyle={th}
      tdStyle={td}
      emptyMessage="No tickets match these filters."
    />
  );
}

export function FocusExplorer({
  widget,
  monthTickets,
  days,
  monthLabel,
  live,
  drill,
  onClose,
}: {
  widget: Widget;
  monthTickets: Row[];
  days: number;
  monthLabel: string;
  live: boolean;
  /** The row picked inside the widget, when the drill-down was opened from one
   *  rather than from the card itself (issue #117). */
  drill?: Drill | null;
  onClose: () => void;
}) {
  // The widget's own saved view, not a live slice: the drill-down reads what the
  // widget is configured to show. Editing those filters lives in the builder.
  const filters = useMemo<Filters>(
    () => ({
      priority: [],
      issueType: [],
      subType: [],
      source: [],
      status: [],
      dayFrom: 1,
      dayTo: days,
      // Seed from what the widget represents (e.g. "Tickets closed" opens on
      // closed tickets); a filter saved onto the widget wins.
      ...seedFiltersForWidget(widget, monthTickets),
      ...(widget.filters || {}),
    }),
    [widget, monthTickets, days],
  );

  // The ticket opened on top of this drill-down, if any. The Tickets page's own
  // modal does the rendering; Escape reaches it first (it listens on document in
  // the capture phase and stops the event), so one Escape closes the ticket and
  // leaves the drill-down standing.
  const [openTicket, setOpenTicket] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The cohort the clicked widget counts — opened this month for everything bar
  // "Tickets completed", which counts work finished this month. The widget's
  // filters apply inside the cohort, so the popout can never list a ticket the
  // tile did not count, and "day" means the day that cohort is about (raised vs
  // finished).
  const cohort = cohortForWidget(widget);
  const base = useMemo(() => cohortRows(cohort, monthTickets), [cohort, monthTickets]);

  // The widget's own filters, then the row the customer actually clicked. The
  // drill is applied last and narrows only — it can never widen the cohort past
  // what the tile counted, so the row's number and this list's count agree.
  const withinWidget = useMemo(() => applyFilters(base, filters, cohort), [base, filters, cohort]);
  // Dropping the picked row without closing the popout: the widget's whole cohort
  // is one click away rather than requiring a close and a re-click on the card.
  // Reset per open — the dashboard keys this component on the drill.
  const [cleared, setCleared] = useState(false);
  const activeDrill = cleared ? null : drill;
  const filtered = useMemo(
    () => (activeDrill ? withinWidget.filter(activeDrill.match) : withinWidget),
    [withinWidget, activeDrill],
  );
  const sorted = useMemo(
    () =>
      filtered
        .slice()
        .sort((a, b) => cohortDay(b, cohort) - cohortDay(a, cohort) || b.hour - a.hour),
    [filtered, cohort],
  );

  return (
    <div style={scrim} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div style={panel} role="dialog" aria-modal="true">
        <div style={head}>
          {/* No "LOCKED" badge. It labelled the drill-down's own internals — the
              board locks onto the clicked widget — and a customer reading it
              beside their ticket data reasonably asked what of theirs was locked
              (issue #115). The widget's name is the useful heading. */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: SANS, fontWeight: 600, fontSize: 19, color: C.ink, letterSpacing: "-0.01em" }}>
                {widgetTitle(widget)}
              </div>
              <div style={{ fontSize: 11.5, color: C.stone, fontFamily: MONO, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                {monthLabel}
                {live && <span style={{ color: C.forest }}>● live</span>}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" style={closeBtn} onClick={onClose}>
              Close ✕
            </button>
          </div>
        </div>

        <div>
          <div style={lowerHead}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              {/* Just "Tickets": the panel already carries the widget's name above,
                  so "Underlying" only added jargon to say these are the ones behind
                  it (issue #115). */}
              <span style={{ fontFamily: SANS, fontWeight: 600, fontSize: 13, color: C.ink }}>Tickets</span>
              {/* Which row was picked. Without it a narrowed list is
                  indistinguishable from the widget's whole cohort, and the count
                  beside it would look simply wrong (issue #117). */}
              {activeDrill && (
                <span style={drillChip}>
                  {activeDrill.label}
                  <button
                    type="button"
                    onClick={() => setCleared(true)}
                    title="Show every ticket behind this widget instead"
                    style={drillChipClear}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
            {/* No longer claims "newest first": that is only the order the table
                OPENS in now that any column can be sorted (issue #121). */}
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.stone }}>
              {filtered.length} of {base.length} · {live ? "live" : "archived period"}
            </span>
          </div>
          <div style={{ padding: "0 22px 18px" }}>
            <ExplorerTable tickets={sorted} onOpenTicket={setOpenTicket} />
          </div>
        </div>
      </div>
      {/* Portalled to the body so the portal-wide modal keeps its own styling
          rather than inheriting the reporting surface's fonts from .rp-root,
          and so it stacks above this drill-down. */}
      {openTicket &&
        createPortal(
          <TicketDetail ticketNumber={openTicket} onClose={() => setOpenTicket(null)} />,
          document.body,
        )}
    </div>
  );
}

const scrim: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(20,20,18,0.42)",
  backdropFilter: "blur(3px)",
  WebkitBackdropFilter: "blur(3px)",
  zIndex: 200,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "32px 24px",
  overflow: "auto",
};
const panel: CSSProperties = {
  width: "min(1180px, 100%)",
  background: "#fff",
  borderRadius: 4,
  boxShadow: "0 24px 80px rgba(20,20,20,0.35)",
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  border: `1px solid ${C.rule}`,
};
const head: CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 6,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  padding: "16px 22px",
  borderBottom: `1px solid ${C.rule}`,
  background: "#fff",
};
const closeBtn: CSSProperties = {
  font: "inherit",
  cursor: "pointer",
  padding: "7px 13px",
  fontSize: 11.5,
  fontFamily: MONO,
  background: C.ink,
  color: "#fff",
  border: "none",
  borderRadius: 2,
};
const drillChip: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  padding: "3px 4px 3px 9px",
  background: "#f0f6ef",
  border: `1px solid ${C.rule}`,
  borderRadius: 2,
  fontFamily: MONO,
  fontSize: 10.5,
  color: C.forest,
  whiteSpace: "nowrap",
  maxWidth: "100%",
  overflow: "hidden",
};
const drillChipClear: CSSProperties = {
  font: "inherit",
  cursor: "pointer",
  background: "none",
  border: "none",
  padding: "0 4px",
  color: C.stone,
  lineHeight: 1,
};
const lowerHead: CSSProperties = {
  position: "sticky",
  top: 57,
  zIndex: 4,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  padding: "12px 22px 10px",
  background: "#fff",
  borderBottom: `1px solid ${C.rule}`,
};
